import { InfoIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import { CircleIconButton } from '@/components/ui/circle-icon-button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { CollapsibleSection, WorkbenchAccordion } from '@/components/workbench';
import type { ConceptVisualDirection } from '@/lib/briefs/concept-contract';
import type { BriefConceptWrite } from '@/services/data/BriefConceptEditorService';

/**
 * The concept brief, re-presented beneath Layers.
 *
 * WHY STACKED AND NOT TABBED. The single highest-value act on this surface is
 * "does this image show the subject the brief asked for?". A tab makes that a
 * two-click comparison against memory. The two panels are phase-disjoint but
 * comparison-coupled: the brief is written before generation and READ constantly
 * during editing, while Layers is meaningless before generation and written
 * constantly during it. Stacking serves the read; tabs would kill it.
 *
 * These are the same controls, the same labels and the same DB homes as
 * `BriefConceptPage` — re-presentation, not re-design — and the save path is that
 * page's own service (`useBriefConcept`), never a second copy of its logic.
 *
 * Rows are LABEL-ABOVE-CONTROL, deliberately unlike the right inspector's
 * label-left/value-right property rows. §5 rows hold enumerable values that read
 * at a glance; these hold prose, and a right-aligned textarea is unreadable.
 */

const VISUAL_INTENTS: Array<{ value: ConceptVisualDirection['intent']; label: string }> = [
  { value: 'none', label: 'No visual direction' },
  { value: 'product_hero', label: 'Product hero' },
  { value: 'in_use', label: 'In use' },
  { value: 'scene', label: 'Scene' },
  { value: 'portrait', label: 'Portrait' },
  { value: 'artefact', label: 'Artifact' },
  { value: 'diagram', label: 'Diagram' },
  { value: 'screenshot', label: 'Screenshot' },
];

const NEGATIVE_SPACE_OPTIONS: Array<{
  value: ConceptVisualDirection['negativeSpace'];
  label: string;
}> = [
  { value: 'none', label: 'No reserved area' },
  { value: 'upper-third', label: 'Upper third' },
  { value: 'lower-third', label: 'Lower third' },
  { value: 'left-column', label: 'Left column' },
  { value: 'right-column', label: 'Right column' },
];

/**
 * `visualDirection.textPlan` has existed, been validated on save and defaulted in
 * the brief-order pipeline for months with NO UI anywhere. It is the switch
 * between "Mora sets the type as editable layers", "the image model renders the
 * words into the pixels" and "no words" — which is precisely the decision this
 * surface exists to make, so it is the highest-leverage control in the column.
 */
const TEXT_PLAN_OPTIONS: Array<{
  value: ConceptVisualDirection['textPlan']['mode'];
  label: string;
}> = [
  { value: 'overlay', label: 'Overlay — Mora sets the type as editable layers' },
  { value: 'baked', label: 'Baked in — the image model renders the words' },
  { value: 'none', label: 'None — image only' },
];

const FIELD_LABEL = 'text-detail text-muted-foreground';

export interface WorkbenchBriefFieldsProps {
  draft: BriefConceptWrite;
  /**
   * Supplemental references the concept was written against. Not part of
   * `BriefConceptWrite` — the RPC takes them as a separate argument — so they
   * arrive as their own prop rather than being smuggled into the write type.
   */
  referenceUrls?: readonly string[];
  onChange: (patch: Partial<BriefConceptWrite> & { referenceUrls?: string[] }) => void;
  disabled?: boolean;
}

export function WorkbenchBriefFields({
  draft,
  referenceUrls,
  onChange,
  disabled = false,
}: WorkbenchBriefFieldsProps): JSX.Element {
  const direction = draft.visualDirection;

  const patchDirection = (patch: Partial<ConceptVisualDirection>) =>
    onChange({ visualDirection: { ...direction, ...patch } });

  return (
    <WorkbenchAccordion defaultOpen={['direction', 'message']} className="px-3 pb-4">
      <CollapsibleSection value="direction" title="Direction">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <span className={FIELD_LABEL}>Intent</span>
            <Select
              value={direction.intent}
              disabled={disabled}
              onValueChange={(value) =>
                patchDirection({ intent: value as ConceptVisualDirection['intent'] })
              }
            >
              <SelectTrigger className="h-8 text-caption">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VISUAL_INTENTS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className={FIELD_LABEL}>Subject</span>
            <Textarea
              value={direction.subject}
              disabled={disabled}
              rows={3}
              className="min-h-12 text-caption"
              onChange={(event) => patchDirection({ subject: event.target.value })}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <span className={FIELD_LABEL}>Setting</span>
            <Textarea
              value={direction.setting}
              disabled={disabled}
              rows={3}
              className="min-h-12 text-caption"
              onChange={(event) => patchDirection({ setting: event.target.value })}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <span className={FIELD_LABEL}>Reserved composition area</span>
            <Select
              value={direction.negativeSpace}
              disabled={disabled}
              onValueChange={(value) =>
                patchDirection({
                  negativeSpace: value as ConceptVisualDirection['negativeSpace'],
                })
              }
            >
              <SelectTrigger className="h-8 text-caption">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {NEGATIVE_SPACE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className={FIELD_LABEL}>Text plan</span>
            <Select
              value={direction.textPlan.mode}
              disabled={disabled}
              onValueChange={(value) =>
                patchDirection({
                  textPlan: {
                    ...direction.textPlan,
                    mode: value as ConceptVisualDirection['textPlan']['mode'],
                  },
                })
              }
            >
              <SelectTrigger className="h-8 text-caption">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TEXT_PLAN_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {direction.refusal ? (
            <p className="flex items-start gap-1.5 text-detail text-muted-foreground">
              <InfoIcon className="mt-px size-3.5 shrink-0" weight="regular" />
              <span>{direction.refusal}</span>
            </p>
          ) : null}
        </div>
      </CollapsibleSection>

      <CollapsibleSection value="message" title="Message">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <span className={FIELD_LABEL}>Hook</span>
            <Input
              value={draft.hook ?? ''}
              disabled={disabled}
              className="h-8 text-caption"
              onChange={(event) => onChange({ hook: event.target.value || null })}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <span className={FIELD_LABEL}>Angle rationale</span>
            <Textarea
              value={draft.rationale ?? ''}
              disabled={disabled}
              rows={3}
              className="min-h-12 text-caption"
              onChange={(event) => onChange({ rationale: event.target.value || null })}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <span className={FIELD_LABEL}>Message direction</span>
            <Textarea
              value={draft.summary ?? ''}
              disabled={disabled}
              rows={3}
              className="min-h-12 text-caption"
              onChange={(event) => onChange({ summary: event.target.value || null })}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <span className={FIELD_LABEL}>Call to action</span>
            <Input
              value={draft.cta ?? ''}
              disabled={disabled}
              className="h-8 text-caption"
              onChange={(event) => onChange({ cta: event.target.value || null })}
            />
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection value="evidence" title="Evidence">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <span className={FIELD_LABEL}>Quoted evidence</span>
            <Textarea
              value={draft.proof.kind === 'none' ? '' : (draft.proof.quote ?? '')}
              disabled={disabled || draft.proof.kind === 'none'}
              rows={3}
              className="min-h-12 text-caption"
              placeholder={draft.proof.kind === 'none' ? 'No proof on this concept' : undefined}
              onChange={(event) =>
                draft.proof.kind === 'none'
                  ? undefined
                  : onChange({ proof: { ...draft.proof, quote: event.target.value } })
              }
            />
          </div>

          {/* REFERENCE URLS. The contract declares them as feeding "evidence
              display, generation", they are editable on the form page, and this
              surface showed them nowhere — not even read-only — while being the
              place generation is actually run. They live in the Sheet, not the
              inspector rail: a URL in a right-aligned 400px property row
              truncates to `https://…` and cannot be read, let alone corrected.
              Proof KIND and SOURCE stay in the rail because they are
              enumerable; this is the same prose/enumerable split the two
              surfaces already draw. */}
          {referenceUrls ? (
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between gap-2">
                <span className={FIELD_LABEL}>Reference URLs</span>
                {!disabled && referenceUrls.length < 5 ? (
                  <Button
                    type="button"
                    variant="flat"
                    size="compact"
                    onClick={() => onChange({ referenceUrls: [...referenceUrls, ''] })}
                  >
                    <PlusIcon data-icon="inline-start" />
                    Add
                  </Button>
                ) : null}
              </div>
              {referenceUrls.length === 0 ? (
                <p className="text-detail text-muted-foreground">No supplemental references.</p>
              ) : (
                referenceUrls.map((url, index) => (
                  <div key={index} className="flex items-center gap-1.5">
                    <Input
                      value={url}
                      disabled={disabled}
                      placeholder="https://"
                      aria-label={`Reference URL ${index + 1}`}
                      className="h-8 text-caption"
                      onChange={(event) =>
                        onChange({
                          referenceUrls: referenceUrls.map((entry, entryIndex) =>
                            entryIndex === index ? event.target.value : entry,
                          ),
                        })
                      }
                    />
                    <CircleIconButton
                      size="sm"
                      ariaLabel={`Remove reference URL ${index + 1}`}
                      disabled={disabled}
                      onClick={() =>
                        onChange({
                          referenceUrls: referenceUrls.filter(
                            (_, entryIndex) => entryIndex !== index,
                          ),
                        })
                      }
                    >
                      <TrashIcon />
                    </CircleIconButton>
                  </div>
                ))
              )}
            </div>
          ) : null}
        </div>
      </CollapsibleSection>
    </WorkbenchAccordion>
  );
}
